// src/components/VPerfectSignature.ts
import { defineComponent } from "vue-demi";
import * as PerfectFreehand from "perfect-freehand";

// src/utils/h-demi.ts
import { h as hDemi, isVue2 } from "vue-demi";
function adaptOnsV3(ons) {
  if (!ons)
    return null;
  return Object.entries(ons).reduce((ret, [key, handler]) => {
    key = key.charAt(0).toUpperCase() + key.slice(1);
    key = `on${key}`;
    return { ...ret, [key]: handler };
  }, {});
}
function h(type, options = {}, children) {
  if (isVue2)
    return hDemi(type, options, children);
  const { props, domProps, on, ...extraOptions } = options;
  const ons = adaptOnsV3(on);
  const params = { ...extraOptions, ...props, ...domProps, ...ons };
  return hDemi(type, params, children);
}
var h_demi_default = h;

// src/utils/get-svg-path-from-stroke.ts
function getSvgPathFromStroke(stroke) {
  if (!stroke.length)
    return "";
  const d = stroke.reduce(
    (acc, [x0, y0], i, arr) => {
      const [x1, y1] = arr[(i + 1) % arr.length];
      acc.push(x0, y0, (x0 + x1) / 2, (y0 + y1) / 2);
      return acc;
    },
    ["M", ...stroke[0], "Q"]
  );
  d.push("Z");
  return d.join(" ");
}

// src/utils/constants.ts
var DEFAULT_BACKGROUND_COLOR = "rgba(0,0,0,0)";
var DEFAULT_PEN_COLOR = "#000";
var DEFAULT_WIDTH = "100%";
var DEFAULT_HEIGHT = "100%";
var IMAGE_TYPES = ["image/png", "image/jpeg", "image/svg+xml"];

// src/components/VPerfectSignature.ts
var { getStroke } = PerfectFreehand;
var VPerfectSignature_default = defineComponent({
  props: {
    width: {
      type: String,
      required: false,
      default: DEFAULT_WIDTH
    },
    height: {
      type: String,
      required: false,
      default: DEFAULT_HEIGHT
    },
    backgroundColor: {
      type: String,
      required: false,
      default: DEFAULT_BACKGROUND_COLOR
    },
    penColor: {
      type: String,
      required: false,
      default: DEFAULT_PEN_COLOR
    },
    strokeOptions: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },
  emits: ["onBegin", "onEnd"],
  data: () => ({
    allInputPoints: [],
    currentInputPoints: null,
    isDrawing: false,
    isLocked: false,
    cachedImages: [],
    ctx: null
  }),
  watch: {
    backgroundColor() {
      this.setBackgroundAndPenColor();
    },
    penColor(color) {
      const ctx = this.getCanvasContext();
      if (ctx)
        ctx.fillStyle = color;
    },
    allInputPoints: {
      deep: true,
      handler() {
        this.inputPointsHandler();
      }
    },
    currentInputPoints: {
      deep: true,
      handler() {
        this.inputPointsHandler();
      }
    }
  },
  mounted() {
    this.resizeCanvas();
  },
  methods: {
    _drawImage(image) {
      const canvas = this.getCanvasElement();
      const ctx = this.getCanvasContext();
      const dpr = window.devicePixelRatio || 1;
      ctx?.scale(1 / dpr, 1 / dpr);
      ctx?.drawImage(image, 0, 0, canvas.width, canvas.height);
      ctx?.scale(dpr, dpr);
    },
    handlePointerDown(e) {
      if (this.isLocked)
        return;
      e.preventDefault();
      const canvas = e.composedPath()[0];
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      this.currentInputPoints = [[x, y, e.pressure]];
      this.isDrawing = true;
      this.$emit("onBegin", e);
    },
    handlePointerMove(e) {
      if (this.isLocked)
        return;
      if (!this.isDrawing)
        return;
      if (e.buttons === 1) {
        e.preventDefault();
        const canvas = e.composedPath()[0];
        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        this.currentInputPoints = [
          ...this.currentInputPoints ?? [],
          [x, y, e.pressure]
        ];
      }
    },
    handlePointerUp(e) {
      if (this.isLocked)
        return;
      e.preventDefault();
      this.isDrawing = false;
      if (!this.currentInputPoints)
        return;
      this.allInputPoints = [...this.allInputPoints, this.currentInputPoints];
      this.currentInputPoints = null;
      this.$emit("onEnd", e);
    },
    handlePointerEnter(e) {
      if (this.isLocked)
        return;
      if (e.buttons === 1)
        this.handlePointerDown(e);
    },
    handlePointerLeave(e) {
      if (this.isLocked)
        return;
      if (!this.isDrawing)
        return;
      this.handlePointerUp(e);
    },
    isEmpty() {
      return !this.allInputPoints.length && !this.cachedImages.length;
    },
    clear() {
      this.cachedImages = [];
      this.allInputPoints = [];
      this.currentInputPoints = null;
    },
    fromData(data) {
      this.allInputPoints = [...this.allInputPoints, ...data];
    },
    lock() {
      this.isLocked = true;
    },
    unlock() {
      this.isLocked = false;
    },
    toData() {
      return this.allInputPoints;
    },
    fromDataURL(data) {
      return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => {
          this._drawImage(image);
          this.cachedImages.push(image);
          resolve(true);
        };
        image.onerror = () => {
          reject(new Error("Incorrect data uri provided"));
        };
        image.crossOrigin = "anonymous";
        image.src = data;
      });
    },
    toDataURL(type) {
      if (type && !IMAGE_TYPES.includes(type)) {
        throw new Error(
          `Incorrect image type. Must be one of ${IMAGE_TYPES.join(", ")}.`
        );
      }
      if (this.isEmpty())
        return;
      const canvas = this.getCanvasElement();
      return canvas.toDataURL(type ?? "image/png");
    },
    getCanvasElement() {
      return this.$refs.signaturePad;
    },
    getCanvasContext() {
      if (!this.ctx) {
        const canvas = this.getCanvasElement();
        this.ctx = canvas.getContext("2d");
      }
      return this.ctx;
    },
    setBackgroundAndPenColor() {
      const canvas = this.getCanvasElement();
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = this.backgroundColor;
      ctx?.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = this.penColor;
    },
    resizeCanvas(clearCanvas = true) {
      const canvas = this.getCanvasElement();
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      const ctx = this.getCanvasContext();
      ctx?.scale(dpr, dpr);
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      if (clearCanvas) {
        ctx?.clearRect(0, 0, canvas.width, canvas.height);
        this.clear();
      }
      this.setBackgroundAndPenColor();
    },
    inputPointsHandler() {
      const canvas = this.getCanvasElement();
      const ctx = this.getCanvasContext();
      ctx?.clearRect(0, 0, canvas.width, canvas.height);
      this.cachedImages.forEach((image) => this._drawImage(image));
      this.setBackgroundAndPenColor();
      this.allInputPoints.forEach((point) => {
        const pathData2 = getSvgPathFromStroke(
          getStroke(point, this.strokeOptions)
        );
        const myPath2 = new Path2D(pathData2);
        ctx?.fill(myPath2);
      });
      if (!this.currentInputPoints)
        return;
      const pathData = getSvgPathFromStroke(
        getStroke(this.currentInputPoints, this.strokeOptions)
      );
      const myPath = new Path2D(pathData);
      ctx?.fill(myPath);
    }
  },
  render() {
    const { width, height } = this;
    const handlers = {
      pointerdown: this.handlePointerDown,
      pointerup: this.handlePointerUp,
      pointermove: this.handlePointerMove,
      pointerenter: this.handlePointerEnter,
      pointerleave: this.handlePointerLeave
    };
    return h_demi_default("canvas", {
      ref: "signaturePad",
      style: {
        height,
        width,
        touchAction: "none"
      },
      on: handlers
    });
  }
});

// src/index.ts
var src_default = VPerfectSignature_default;
export {
  VPerfectSignature_default as VPerfectSignature,
  src_default as default
};
//# sourceMappingURL=index.js.map